import React from "react"
import { Box } from "@chakra-ui/react"
import {
  BIKE_SERVICE_KEY,
  BIKE_MAINTENANCE_KEY,
  BIKE_DROPZONE_KEY,
  TOPBAR_HEIGHT,
  NAV_WIDTH,
} from "../lib/config"
import { Bbox } from "../lib/helpers/map"
import { TripEntity } from "../lib/types/trip"
import {
  useGetMaintenanceZonesQuery,
  useGetServiceZonesQuery,
  useGetDropzonesQuery,
} from "../lib/hooks/queries/useGetMapZones"
import { useStoredDisclosure } from "../lib/hooks/useStoredDisclosure"
import { ErrorBoundary } from "./ErrorBoundary"
import { MapZoneControl } from "./MapZoneControl"
import TripsMapGoogleMap from "./TripsMapGoogleMap"
import { TripsMapMapbox } from "./TripsMapMapbox"
import { useMapSettings } from "./providers/MapSettingsProvider"

interface TripsMapProps {
  bbox: Bbox
  trips: TripEntity[]
  selectedTrip?: TripEntity
  onSelectTrip: (trip: TripEntity) => void
}

const TripsMap: React.FC<TripsMapProps> = ({ bbox, trips, selectedTrip, onSelectTrip }) => {
  const { mapProvider } = useMapSettings()

  const { isOpen: showServiceZones, onToggle: onServiceZoneToggle } = useStoredDisclosure({
    key: BIKE_SERVICE_KEY,
    defaultIsOpen: true,
  })
  const { isOpen: showDropzones, onToggle: onDropzoneToggle } = useStoredDisclosure({
    key: BIKE_DROPZONE_KEY,
    defaultIsOpen: true,
  })
  const { isOpen: showMaintenanceZones, onToggle: onMaintenanceToggle } = useStoredDisclosure({
    key: BIKE_MAINTENANCE_KEY,
    defaultIsOpen: true,
  })

  const { serviceZones } = useGetServiceZonesQuery({ enabled: showServiceZones })
  const { dropzones } = useGetDropzonesQuery({ enabled: showDropzones })
  const { maintenanceZones } = useGetMaintenanceZonesQuery({ enabled: showMaintenanceZones })

  return (
    <ErrorBoundary>
      <Box
        position="relative"
        style={{
          height: `calc(100vh - ${TOPBAR_HEIGHT})`,
          width: `calc(100vw - ${NAV_WIDTH})`,
        }}
      >
        <ErrorBoundary>
          {mapProvider === "google_maps" ? (
            <TripsMapGoogleMap
              bbox={bbox}
              trips={trips}
              selectedTrip={selectedTrip}
              onSelectTrip={onSelectTrip}
              serviceZones={serviceZones}
              dropzones={dropzones}
              maintenanceZones={maintenanceZones}
              showDropzones={showDropzones}
              showServiceZones={showServiceZones}
              showMaintenanceZones={showMaintenanceZones}
            />
          ) : (
            <TripsMapMapbox
              bbox={bbox}
              trips={trips}
              selectedTrip={selectedTrip}
              onSelectTrip={onSelectTrip}
              serviceZones={serviceZones}
              dropzones={dropzones}
              maintenanceZones={maintenanceZones}
              showDropzones={showDropzones}
              showServiceZones={showServiceZones}
              showMaintenanceZones={showMaintenanceZones}
            />
          )}
        </ErrorBoundary>
        <MapZoneControl
          showDropzones={showDropzones}
          showServiceZones={showServiceZones}
          showMaintenanceZones={showMaintenanceZones}
          onDropzoneToggle={onDropzoneToggle}
          onServiceZoneToggle={onServiceZoneToggle}
          onMaintenanceToggle={onMaintenanceToggle}
        />
      </Box>
    </ErrorBoundary>
  )
}

export default TripsMap
